import {
  Grid,
  MenuItem,
  Typography,
  FormControl,
  TableContainer,
  Table,
  TableBody,
  TableRow,
  TableHead,
  Box,
  FormHelperText,
  TablePagination,
  TextField,
  InputAdornment,
  LinearProgress,
  Stack,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { React, useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import {
  StyledSelect,
  StyledTextField,
} from "../../customComponents/styled/styledInputs";
import { StyledPaper } from "../../customComponents/styled/styledPaper";
import { StyledLoadingButton } from "../../customComponents/styled/styledButtons";
import {
  StyledTableCell,
  StyledTableRow,
} from "../../customComponents/styled/styledTable";
import { useFormik } from "formik";
import * as yup from "yup";
import FeesScheduleDialog from "./feesScheduleDialog";
import { StyledMultiSelect } from "../../customComponents/styled/customMultiSelect";
import {
  useGetAllClassroomsQuery,
  useGetClassesPaginatedQuery,
} from "../classroom/classApiSlice";
import { useGetSessionsPaginatedQuery } from "../settings/session/sessionApiSlice";
import { useCreateFeeMutation } from "./feeScheduleApiSlice";
import toast from "../../../helpers/toast";

const validationSchema = yup.object({
  class: yup.array().required("Required"),
  termName: yup.string(),
  gender: yup.string(),
  name: yup.string().required("Required"),
  amount: yup.string().required("Required"),
  compulsory: yup.string().required("Required"),
});

function FeesSchedule() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [classesPage, setClassesPage] = useState(0);
  const [classesPerPage, setClassesPerPage] = useState(10);
  const [classSearch, setClassSearch] = useState("");

  const { data: allClassrooms } = useGetAllClassroomsQuery();
  const { data: classroomsData, isFetching: classesLoading } =
    useGetClassesPaginatedQuery({
      page: classesPage + 1,
      per_page: classesPerPage,
      q: classSearch,
    });

  const { data: sessionsData } = useGetSessionsPaginatedQuery({ page: 1, per_page: 100 });
  const termNameOptions = Array.from(
    new Set(
      (sessionsData?.sessions ?? [])
        .flatMap((session) => session.terms ?? [])
        .map((term) => term.name)
        .filter(Boolean)
    )
  );
  const classrooms = classroomsData?.classrooms ?? [];
  const totalClasses = classroomsData?.pagination?.total_items ?? 0;
  const [createFee, { isLoading: createFeeLoading }] = useCreateFeeMutation();

  const formik = useFormik({
    initialValues: {
      class: [],
      termName: "",
      gender: "",
      name: "",
      amount: "",
      compulsory: "",
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      values.class?.forEach(async (item) => {
        try {
          let body = {
            name: values.name,
            amount: values.amount,
            is_compulsory: values.compulsory,
            classroom_id: item.value,
            term_name: values.termName || null,
            gender: values.gender || null,
          };
          const response = await createFee(body).unwrap();
          if (response.id) {
            // Only clear the per-item fields — class/term/gender/compulsory
            // stay put so adding several fee items to the same class/term
            // doesn't require re-picking them each time.
            formik.resetForm({ values: { ...values, name: "", amount: "" } });
            toast.success("Fee Created Successfully");
          }
        } catch (error) {
          toast.error("Error Creating Fee");
        }
      });
    },
  });

  function multiHandleChange(selectedOption) {
    formik.setFieldValue("class", selectedOption);
  }

  const [openEdit, setOpenEdit] = useState(false);
  const [selectedClass, setSelectedClass] = useState(false);

  return (
    <Box sx={{ px: { xs: 0, sm: 1 } }}>
      <Grid container columnSpacing={4}>
        <Grid item xs={12} md={4}>
          <StyledPaper sx={{ padding: "15px" }}>
            <Typography variant="formHeading" sx={{ marginBottom: "20px" }}>
              Fees Schedule
            </Typography>

            <Box component="form" onSubmit={formik.handleSubmit}>
              <Grid container rowSpacing={3}>
                <Grid item xs={12} md={12}>
                  <FormControl
                    error={formik.touched.class && formik.errors.class}
                    fullWidth
                  >
                    <Typography variant="formSubHeading">CLASS</Typography>
                    <StyledMultiSelect
                      id="class"
                      name="class"
                      onChange={multiHandleChange}
                      value={formik.values.class}
                      error={
                        formik.touched.class && Boolean(formik.errors.class)
                      }
                      options={allClassrooms?.map((classroom) => ({
                        label: classroom.name,
                        value: classroom.id,
                      }))}
                      placeholder="Select a class"
                    />

                    <FormHelperText variant="error">
                      {formik.touched.class && formik.errors.class}
                    </FormHelperText>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={12}>
                  <FormControl
                    error={formik.touched.item && formik.errors.item}
                    fullWidth
                  >
                    <Typography variant="formSubHeading">Name</Typography>
                    <StyledTextField
                      id="name"
                      name="name"
                      value={formik.values.name}
                      onChange={formik.handleChange}
                      error={formik.touched.name && Boolean(formik.errors.name)}
                    />
                    <FormHelperText variant="error">
                      {formik.touched.name && formik.errors.name}
                    </FormHelperText>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={12}>
                  <FormControl
                    error={formik.touched.amount && formik.errors.amount}
                    fullWidth
                  >
                    <Typography variant="formSubHeading">AMOUNT</Typography>
                    <StyledTextField
                      type="number"
                      id="amount"
                      name="amount"
                      value={formik.values.amount}
                      onChange={formik.handleChange}
                      error={
                        formik.touched.amount && Boolean(formik.errors.amount)
                      }
                    />
                    <FormHelperText variant="error">
                      {formik.touched.amount && formik.errors.amount}
                    </FormHelperText>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={12}>
                  <FormControl fullWidth>
                    <Typography variant="formSubHeading">TERM</Typography>
                    <StyledSelect
                      id="termName"
                      name="termName"
                      value={formik.values.termName}
                      onChange={formik.handleChange}
                      displayEmpty
                    >
                      <MenuItem value="">All Terms</MenuItem>
                      {termNameOptions.map((name) => (
                        <MenuItem key={name} value={name}>{name}</MenuItem>
                      ))}
                    </StyledSelect>
                    <FormHelperText>
                      Matched by name — a future term called "{formik.values.termName || "First Term"}" will reuse this fee.
                    </FormHelperText>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={12}>
                  <FormControl fullWidth>
                    <Typography variant="formSubHeading">
                      GENDER (OPTIONAL)
                    </Typography>
                    <StyledSelect
                      id="gender"
                      name="gender"
                      onChange={formik.handleChange}
                      value={formik.values.gender}
                      displayEmpty
                    >
                      <MenuItem value="">All students</MenuItem>
                      <MenuItem value="Male">Male only</MenuItem>
                      <MenuItem value="Female">Female only</MenuItem>
                    </StyledSelect>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={12}>
                  <FormControl
                    error={
                      formik.touched.compulsory && formik.errors.compulsory
                    }
                    fullWidth
                  >
                    <Typography variant="formSubHeading">
                      IS COMPULSORY
                    </Typography>
                    <StyledSelect
                      id="compulsory"
                      name="compulsory"
                      onChange={formik.handleChange}
                      value={formik.values.compulsory}
                      error={
                        formik.touched.compulsory &&
                        Boolean(formik.errors.compulsory)
                      }
                    >
                      <MenuItem value={true}>Yes</MenuItem>
                      <MenuItem value={false}>No</MenuItem>
                    </StyledSelect>
                    <FormHelperText variant="error">
                      {formik.touched.compulsory && formik.errors.compulsory}
                    </FormHelperText>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={12} textAlign={"right"}>
                  <StyledLoadingButton
                    loading={createFeeLoading}
                    type="submit"
                    sx={{ textTransform: "none" }}
                  >
                    Save
                  </StyledLoadingButton>
                </Grid>
              </Grid>
            </Box>
          </StyledPaper>
        </Grid>

        {/* Generated Table */}

        <Grid item xs={12} md={8}>
          <StyledPaper sx={{ paddingBottom: "25px" }}>
            <FeesScheduleDialog
              open={openEdit}
              setOpen={setOpenEdit}
              selectedClass={selectedClass}
            />
            <Box sx={{ padding: "15px" }}>
              <Typography variant="formHeading">Classes</Typography>
            </Box>
            <Box sx={{ px: 2, pb: 1 }}>
              <TextField
                size="small"
                fullWidth
                placeholder="Search classes..."
                value={classSearch}
                onChange={(e) => {
                  setClassSearch(e.target.value);
                  setClassesPage(0);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            {isMobile ? (
              <Box sx={{ p: 2 }}>
                {classesLoading && <LinearProgress />}
                {classrooms?.map((classroom) => (
                  <Box key={classroom.id} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, p: 1.5, mb: 1.5 }}>
                    <Typography sx={{ color: "primary.main", fontWeight: 600 }}>
                      {classroom?.name}
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                      <Typography
                        className="tableCellLink"
                        sx={{ color: "primary.safeOnLight", fontWeight: 500 }}
                        onClick={() => {
                          setOpenEdit(true);
                          setSelectedClass(classroom);
                        }}
                      >
                        Manage fees
                      </Typography>
                    </Stack>
                  </Box>
                ))}
              </Box>
            ) : (
              <TableContainer sx={{ overflowX: "auto" }}>
                {classesLoading && <LinearProgress />}
                <Table>
                  <TableHead>
                    <TableRow sx={{ borderBottom: "1.5px solid lightgrey " }}>
                      <StyledTableCell>CLASSROOMS</StyledTableCell>
                      {/* <StyledTableCell>TERM</StyledTableCell>
                      <StyledTableCell>FEES</StyledTableCell> */}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {classrooms?.map((classroom) => (
                      <StyledTableRow key={classroom.id}>
                        <StyledTableCell component="th" scope="row">
                          <Box
                            className="tableCellLink"
                            onClick={() => {
                              setOpenEdit(true);
                              setSelectedClass(classroom);
                            }}
                          >
                            {classroom?.name}
                          </Box>
                        </StyledTableCell>
                      </StyledTableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            <TablePagination
              component="div"
              count={totalClasses}
              page={classesPage}
              rowsPerPage={classesPerPage}
              rowsPerPageOptions={[10, 20, 50]}
              onPageChange={(_, newPage) => setClassesPage(newPage)}
              onRowsPerPageChange={(e) => {
                setClassesPerPage(parseInt(e.target.value, 10));
                setClassesPage(0);
              }}
              sx={{ '.MuiTablePagination-toolbar': { flexWrap: 'wrap', minWidth: 0, px: 0 }, '.MuiTablePagination-spacer': { display: 'none' } }}
            />
          </StyledPaper>
        </Grid>
      </Grid>
    </Box>
  );
}

export default FeesSchedule;
